import { PageHero, Reveal, Chapter } from '../lib/motion';
import { CONTACT_EMAIL } from '../data/site';
import { useSEO } from '../lib/seo';

const sections = [
  {
    no: '01',
    label: 'The service',
    title: 'What Vox Meridian is.',
    paras: [
      'Vox Meridian is an introduction service for industrial and business services, serving buyers and providers in Singapore, Malaysia, the UAE, the United Kingdom, Australia, the United States, Canada, South Africa, India and across Europe. Buyers describe the work they need; the founder personally introduces them to a small number of independent service providers suited to that work. Providers receive enquiries matched to their service and market.',
      'Vox Meridian is not a contractor, not an agent of either side, and not a party to any agreement a buyer and provider reach. Any quotation, contract, purchase order or job is strictly between the buyer and the provider.',
    ],
  },
  {
    no: '02',
    label: 'The pilot phase',
    title: 'Free for buyers. Nothing billed to providers.',
    paras: [
      'During the concierge pilot phase, posting an RFQ is free for buyers, and providers pay nothing. Providers accepted as founding members reserve the right to a SGD 29 per month subscription rate, honoured for as long as their subscription remains active once paid plans begin.',
      'Billing for provider subscriptions starts only when the full platform launches, with reasonable notice given beforehand. Reserving a founding spot creates no obligation to subscribe later.',
    ],
  },
  {
    no: '03',
    label: 'Your responsibilities',
    title: 'Accurate information, lawful use.',
    paras: [
      'You confirm that the details you submit are accurate, that you are authorised to share any drawings or documents you upload, and that you will use the introductions made through Vox Meridian lawfully and in good faith.',
      'Please do not submit another company’s confidential documents without permission, and do not use the service to harvest provider or buyer details for unrelated marketing.',
    ],
  },
  {
    no: '04',
    label: 'What we do not promise',
    title: 'Honest limits, stated plainly.',
    paras: [
      'Every genuine RFQ is reviewed by the founder and matched to three to five suitable providers, targeting 24–48 hours — that is a target, not a guarantee, and it depends on provider availability in your trade and country. Some trades are thin during launch, and sometimes the honest answer is that more sourcing time is needed. We will always tell you which it is.',
      'Provider verification is performed on a reasonable-efforts basis as each provider joins: business registry checks appropriate to each country (for example ACRA in Singapore, SSM in Malaysia, or trade licences in the UAE), plus safety credentials and insurance where applicable. It is a genuine check, but it is not a guarantee of any provider’s work, solvency or conduct. Buyers should apply their own normal procurement diligence before awarding work.',
      'Provider availability varies by market. Our network is most established in Singapore and Malaysia; in the other six countries provider recruitment is ongoing during the pilot phase. Where no suitable provider is yet available for your trade and country, the founder sources candidates by hand and tells you honestly how long that may take.',
    ],
  },
  {
    no: '05',
    label: 'Liability',
    title: 'The work belongs to those who do it.',
    paras: [
      'During this pilot phase the service is provided as-is. Vox Meridian is not responsible for the quality, safety, timing or cost of work performed by providers, nor for the accuracy of quotations, and accepts no liability for losses arising from introductions made through the service.',
      'Nothing in these terms excludes liability that cannot be excluded by law.',
    ],
  },
  {
    no: '06',
    label: 'Changes and contact',
    title: 'Terms that evolve, with notice.',
    paras: [
      'These terms apply to the concierge pilot phase and will evolve when the full platform launches. Registered providers and recent buyers will be notified by email before any change takes effect.',
      `Questions about these terms: ${CONTACT_EMAIL} — the founder reads every message personally.`,
    ],
  },
];

const Terms = () => {
  useSEO({
    title: 'Terms of Service — Pilot Phase | Vox Meridian',
    description: 'Plain-English pilot terms: Vox Meridian introduces buyers and verified providers across 10 markets. Free for buyers; providers are billed only at launch.',
    path: '/terms',
  });

  return (
    <div data-testid="terms-page">
      <PageHero
        eyebrow="Terms of service"
        lines={[<>The deal,</>, <span key="l2">stated <span className="text-volt">plainly.</span></span>]}
        sub="Pilot-phase terms for buyers and providers on Vox Meridian. Short enough to actually read — that is deliberate."
      />
      <section className="bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="max-w-3xl space-y-16">
            {sections.map((s) => (
              <div key={s.no} data-testid={`terms-section-${s.no}`}>
                <Chapter no={s.no} label={s.label} title={s.title} />
                <div className="mt-8 space-y-5">
                  {s.paras.map((p, i) => (
                    <Reveal key={i} delay={i * 0.06}>
                      <p className="text-base leading-relaxed text-steel">{p}</p>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
            <Reveal>
              <div className="border border-line bg-surface p-8">
                <p className="mono-label text-volt">Last updated</p>
                <p className="mt-3 text-sm leading-relaxed text-steel">
                  October 2026 · Concierge pilot phase · Questions: <a href={`mailto:${CONTACT_EMAIL}`} data-testid="terms-contact-link" className="text-parchment underline transition-colors duration-200 hover:text-volt">{CONTACT_EMAIL}</a>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
